import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import React, { useState } from 'react';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import { HITL3_PADDING } from '../../utils/hitl3Spacing';
import getStatusBarColor from '../../utils/getStatusBarColor';
import ConsoleCard from '../MainPageLayout/ConsoleCard';
import MenuBoardTimeTile from './MenuBoardTimeTile';
import ResponseTimeTile from './ResponseTimeTile';
import TurnTile from './TurnTile';
import SentimentTile from './SentimentTile';
import PulseSignals from './PulseSignals';
import PulseAdvise from './PulseAdvise';
import { useAppSelector } from '../../app/hooks';
import {
  selectAIStatus,
  selectShowsTakeoverAlert,
  selectIsAIEscalation,
  selectAIEscalationReason,
  selectIsAIEscalatedOnCarEnter,
  selectIsAIAutoMode,
} from '../../redux/features/ai/ai.selector';
import {
  AIStatusColors,
  DEFAULT_STATUS_MESSAGE,
} from '../../redux/features/ai/ai.constants';
import { selectSessionId } from '../../redux/features/sessionBoundary/sessionBoundary.selectors';
import { selectAIIndicator } from '../../selectors/restaurant';
import useIntervention from '../../hooks/intervention/useIntervention.hooks';

/**
 * CUSTOMER PULSE (left pane, top card).
 *
 * A collapsible card that gives the human a 2-second read of the live order's AI
 * status. The header (icon + title + collapse chevron) sits inside the white card
 * (matching Action Center); a full-bleed status band pinned at the card's bottom
 * edge encodes the LIVE AI status — green/yellow/red soft tints, plus a prominent
 * vivid blink on takeover.
 *
 * Under the header the card is a compact strip: the Menu Board Time + Response
 * time meters, the live signal + reminder chips (PulseSignals), and the folded-in
 * AI co-pilot ask (PulseAdvise) — with the AI status band pinned at the bottom.
 */

const useStyles = makeStyles((theme: Theme) => ({
  // AI status band (folded-in AIStatusBar): a full-bleed strip pinned at the
  // card's bottom edge (marginTop:auto pushes it down in the flex column).
  // Background encodes the LIVE AI status and is applied inline via the shared
  // getStatusBarColor() so it matches the legacy AIStatusBar exactly (vivid
  // green/yellow/red), plus a prominent vivid blink for a takeover
  // (bandTakeover). The card padding is 0, so the band already spans edge-to-edge
  // (no negative margins needed); the shared 8px inset pads its text on all sides.
  statusBand: {
    flexShrink: 0,
    marginTop: 'auto',
    padding: HITL3_PADDING,
    // Text styled like the "Continue" intent pill: medium weight, shared hitl3 size.
    fontSize: HITL3_FONT_SIZE,
    fontWeight: 500,
    lineHeight: 1.3,
    // Left-aligned text, vertically centered within the band's available height.
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'hidden',
  },
  // One line: truncate with an ellipsis (no wrap, no h-scroll). minWidth:0 lets
  // this flex child shrink so the ellipsis engages; the full message shows on
  // hover via the title attribute on the band.
  statusText: {
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // Takeover: a prominent vivid red throb (light↔dark) with white text — a
  // "must not miss" alert, so intentionally NOT a soft tint. Static $keyframes
  // reference (JSS resolves the scoped name only in a static rule).
  '@keyframes statusTakeoverPulse': {
    '0%, 100%': { backgroundColor: '#ff5c5c' },
    '50%': { backgroundColor: '#990000' },
  },
  bandTakeover: {
    color: Colors.white,
    animation: '$statusTakeoverPulse 1s ease-in-out infinite',
  },
  // HITL 3.0 metric tiles row, laid out side-by-side under the header. Each tile
  // self-gates to null, so the row can be empty until one becomes active.
  // flex-start (not center) tops-align the tiles so every tile's value + label
  // sit on the same line — tiles with a progress bar (Menu Board Time, Response
  // time) are taller than those without (Turn, Sentiment), and centering would
  // otherwise push the shorter tiles' text down.
  metricRow: {
    marginTop: HITL3_PADDING,
    paddingLeft: HITL3_PADDING,
    paddingRight: HITL3_PADDING,
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
}));

interface CustomerPulseProps {
  // Optional controlled collapse so the parent can drive the resizable panel
  // (collapsing frees its space for the transcript). Falls back to internal
  // state (default open) when used standalone.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const CustomerPulse = ({
  open: openProp,
  onOpenChange,
}: CustomerPulseProps = {}) => {
  const classes = useStyles();
  const [openState, setOpenState] = useState(true);
  const open = openProp ?? openState;
  const toggleOpen = () => {
    const next = !open;
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };

  // ── AI status band — wired to the live AI status ────────────────────────────
  // Precedence (highest first), all gated on an ACTIVE SESSION:
  //
  //   condition                           band shows              colour
  //   ──────────────────────────────────  ──────────────────────  ──────────────
  //   staff intervention                  "Staff Intervention"    torch red
  //   agent intercepted (isAIAutoMode 0)  "Agent Intercept"       red (static)
  //   takeover requested, not yet taken   "take over now" (blink) red (pulsing)
  //   escalation (recoverable), pre-take  escalation reason       red / orange
  //   otherwise                           live AI status          green/yellow/red
  const aiStatus = useAppSelector(selectAIStatus);
  const isAIEscalation = useAppSelector(selectIsAIEscalation);
  const aiEscalationReason = useAppSelector(selectAIEscalationReason);
  const isAIEscalatedOnCarEnter = useAppSelector(selectIsAIEscalatedOnCarEnter);
  const isAIAutoMode = useAppSelector(selectIsAIAutoMode);
  const sessionId = useAppSelector(selectSessionId);
  const aiEnabled = useAppSelector(selectAIIndicator);
  const { isStaffInterventionHappening } = useIntervention();

  // ── Why the band gates on the SESSION, not the car ──────────────────────────
  // The AI control state is deliberately sticky — resetting it on car_exit caused
  // a prod incident (dropped order + cart revert). A drive-thru takeover lets the
  // car leave (car_exit) while the SAME session's order is still being finished,
  // so the band must follow an active session, NOT car presence:
  //
  //   signal            cleared on car_exit?          role
  //   ────────────────  ────────────────────────────  ─────────────────────────
  //   isAIAutoMode      no — sticky to session end     agent-intercepted flag
  //   requiresTakeover  no — sticky to session end     takeover alert
  //   isAIEscalation    no — sticky to session end     escalation flag
  //   sessionId         no while an order is pending   "is the session live?"
  //   isCarPresent      YES, immediately               (removed — too blunt)
  //
  // car_exit with a pending order only arms the post-car-exit timer; sessionId
  // clears only when the session TRULY ends (Finish/Cancel, final check, empty-
  // cart car_exit, kill switch, timeout). Gating on sessionId holds the intercept/
  // escalation status until the session ends, then idles to green.
  const sessionActive = Boolean(sessionId);

  // ── Interception & takeover ─────────────────────────────────────────────────
  // ANY agent interception (isAIAutoMode → false) during an active session shows
  // a static "Agent Intercept" — not just hard takeovers. A takeover escalation
  // ADDITIONALLY blinks a "take over now" alert while the AI is still in control,
  // before the agent acts:
  //
  //   state                                   band
  //   ──────────────────────────────────────  ───────────────────────────────
  //   takeover requested, not yet taken over  BLINKS "take over now" (showPulse)
  //   agent intercepted (any escalation)      static "Agent Intercept"
  //
  // isAIAutoMode and requiresTakeover are both sticky, so these hold for the rest
  // of the session. The blink stays gated on the takeover flag + unresolved-items
  // feature; the plain "Agent Intercept" label does not.
  //
  // HITL-1650 moved that condition into `selectShowsTakeoverAlert`, staff
  // intervention included. It is no longer only a display decision: the
  // auto-switch into `Intercept` fires on the same predicate, and the band and
  // the switch have to agree about what "the AI is asking for a human" means or
  // the console says one thing and does another.
  const agentIntercepted = !isAIAutoMode && sessionActive;
  const showEscalation = isAIEscalation && sessionActive;
  // Between sessions (no active session) the band idles on the default green —
  // the live aiStatus/escalation describe a conversation that is over.
  const displayedAIStatus = sessionActive
    ? aiStatus
    : { status: AIStatusColors.green, message: DEFAULT_STATUS_MESSAGE };

  // ── Pure-HITL parity ────────────────────────────────────────────────────────
  // The legacy AIStatusBar mounted only for AI restaurants (isAIActive), so a
  // pure-HITL restaurant never showed an AI status bar. Match that here:
  //
  //   restaurant    band?   staff-intervention cue
  //   ────────────  ──────  ──────────────────────────────
  //   AI enabled    yes     band + omnibar red border
  //   pure HITL     no      omnibar red border only
  const showStatusBand = aiEnabled;

  // Staff intervention outranks every AI status: a human now owns the order, so
  // the band reads "Staff Intervention" (static, no blink). Otherwise prefer the
  // escalation reason when one is set, falling back to the live status message —
  // which displayedAIStatus.message already resolves to (the green default when
  // idle), so it is the single reliable fallback for every non-flagged state.
  const statusMessage = isStaffInterventionHappening
    ? 'Staff Intervention'
    : agentIntercepted
    ? 'Agent Intercept'
    : (showEscalation && aiEscalationReason) || displayedAIStatus.message;

  // Suppressed during staff intervention — the static "hand-off" red is the
  // resolved, unambiguous state. That term lives inside the selector now.
  const showTakeoverBlink = useAppSelector(selectShowsTakeoverAlert);

  // ── Band background / text colour (matches legacy AIStatusBar) ───────────────
  //   state                background          text
  //   ───────────────────  ──────────────────  ─────────────────────────────
  //   staff intervention   torch red           white
  //   agent intercepted    red (static)        white
  //   escalation / status  getStatusBarColor   white on red/orange, else inherit
  //
  // showTakeoverBlink drives the vivid takeover blink via the bandTakeover class
  // instead of these colours; agentIntercepted is a HITL 3.0-only static state (the
  // legacy bar unmounts on intercept) rendered in the same vivid escalation red.
  const bandBackground = isStaffInterventionHappening
    ? Colors.torchRed
    : agentIntercepted
    ? Colors.red
    : getStatusBarColor(
        showEscalation,
        isAIEscalatedOnCarEnter,
        displayedAIStatus.status
      );
  const bandTextColor =
    isStaffInterventionHappening || showEscalation || agentIntercepted
      ? Colors.white
      : 'inherit';

  return (
    <ConsoleCard
      icon={<AutoAwesomeOutlinedIcon />}
      title="AI Copilot"
      open={open}
      onToggle={toggleOpen}
      testId="customer-pulse"
      footer={
        // Band renders only for AI restaurants (showStatusBand). When shown it is
        // ALWAYS present — the live status / takeover blink must stay visible even
        // when the card is collapsed to a strip; marginTop:auto pins it to the
        // bottom edge. Pure HITL → no band (undefined); the omnibar red border is
        // the staff-intervention cue there.
        showStatusBand ? (
          <div
            data-testid="ai-status-band"
            data-blink={showTakeoverBlink}
            className={`${classes.statusBand}${
              showTakeoverBlink ? ` ${classes.bandTakeover}` : ''
            }`}
            style={
              showTakeoverBlink
                ? undefined
                : { backgroundColor: bandBackground, color: bandTextColor }
            }
            title={statusMessage}
          >
            <span className={classes.statusText}>{statusMessage}</span>
          </div>
        ) : undefined
      }
    >
      {/* HITL 3.0 Pulse strip: the metric tiles (side by side) → the signal +
          reminder chips → the folded-in AI ask. The whole console is already
          gated on hitl_3_0 at the mount site (Body), so no local flag branch is
          needed. Each tile self-gates:
            • Menu Board Time — while a session is active; the car's dwell gauge.
            • Turn — active session + AI enabled; the ai_session_state turn count
              (no progress bar).
            • Sentiment — active session + AI enabled; the ai_session_state
              sentiment (colour + face, muted "—" default).
            • Response time — active session + (agent intercepted OR pure HITL);
              the per-turn reply SLA.
          PulseSignals / PulseAdvise self-gate to null when there's nothing to
          surface.

          During staff intervention the whole body is blanked — the metrics,
          signals and AI ask all describe an AI-run order that no longer applies
          once a human takes over. Only the AI status band (footer) stays, now
          reading "Staff Intervention". */}
      {!isStaffInterventionHappening && (
        <>
          <div className={classes.metricRow}>
            <MenuBoardTimeTile />
            <TurnTile />
            <SentimentTile />
            <ResponseTimeTile />
          </div>
          <PulseSignals />
          <PulseAdvise />
        </>
      )}
    </ConsoleCard>
  );
};

export default CustomerPulse;
